import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/authContext';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../utils/firebase';

const StripeConnectSetup = () => {
  const { user } = useAuth();
  const [connectStatus, setConnectStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settingUp, setSettingUp] = useState(false);
  const [error, setError] = useState(null);
  const [isAlreadySetUp, setIsAlreadySetUp] = useState(false);

  // Check current Connect account status
  const checkConnectStatus = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const getStripeConnectStatus = httpsCallable(functions, 'getStripeConnectStatus');
      const result = await getStripeConnectStatus();
      
      setConnectStatus(result.data);
    } catch (error) {
      console.error('Error checking Connect status:', error);
      setError('Failed to check payment setup status');
    } finally {
      setLoading(false);
    }
  };

  // Set up Stripe Connect account
  const setupConnectAccount = async () => {
    if (!user) return;
    
    try {
      setSettingUp(true);
      setError(null);
      
      // Create Stripe Connect account and get onboarding URL
      const createStripeConnectAccount = httpsCallable(functions, 'createStripeConnectAccount');
      const result = await createStripeConnectAccount({
        returnUrl: window.location.origin + '/home'
      });
      
      if (result.data?.url) {
        // Redirect to Stripe onboarding
        window.location.href = result.data.url;
      } else if (result.data?.alreadySetUp) {
        // User already has account set up
        setIsAlreadySetUp(true);
      } else {
        throw new Error('Failed to create onboarding link');
      }
    } catch (error) {
      console.error('Error setting up Connect account:', error);
      setError('Failed to set up payment account. Please try again.');
    } finally {
      setSettingUp(false);
    }
  };

  useEffect(() => {
    checkConnectStatus();
  }, [user]);

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-700 rounded w-1/4 mb-4"></div>
          <div className="h-3 bg-gray-700 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (isAlreadySetUp) {
    return (
      <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-6">
        <div className="flex items-start">
          <div className="text-green-400 text-xl mr-3">✅</div>
          <div>
            <h3 className="text-green-300 font-semibold mb-2">Payment Account Ready</h3>
            <p className="text-gray-300 text-sm mb-4">
              Your payment account is already set up and ready to receive payments from the marketplace.
            </p>
            <button
              onClick={() => setIsAlreadySetUp(false)}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded text-sm"
            >
              Check Status
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-6">
        <div className="flex items-start">
          <div className="text-red-400 text-xl mr-3">⚠️</div>
          <div>
            <h3 className="text-red-300 font-semibold mb-2">Setup Error</h3>
            <p className="text-gray-300 text-sm mb-4">{error}</p>
            <button
              onClick={checkConnectStatus}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Account is fully set up and ready
  if (connectStatus?.chargesEnabled && connectStatus?.payoutsEnabled) {
    return (
      <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-6">
        <div className="flex items-start">
          <div className="text-green-400 text-xl mr-3">✅</div>
          <div>
            <h3 className="text-green-300 font-semibold mb-2">Payment Account Ready</h3>
            <p className="text-gray-300 text-sm">
              Your payment account is fully set up! You'll automatically receive 50% of your marketplace game sales.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Account exists but needs completion
  if (connectStatus?.accountId && !connectStatus?.chargesEnabled) {
    return (
      <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-6">
        <div className="flex items-start">
          <div className="text-yellow-400 text-xl mr-3">⏳</div>
          <div>
            <h3 className="text-yellow-300 font-semibold mb-2">Almost Ready - Complete Your Bank Setup</h3>
            <p className="text-gray-300 text-sm mb-4">
              You started connecting your bank account but didn't finish. Just complete the last few steps to start earning from marketplace sales.
            </p>
            <button
              onClick={setupConnectAccount}
              disabled={settingUp}
              className="bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50 text-white px-4 py-2 rounded text-sm"
            >
              {settingUp ? 'Completing...' : 'Finish Bank Setup'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // No account exists - needs initial setup
  return (
    <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-6">
      <div className="flex items-start">
        <div className="text-blue-400 text-xl mr-3">💰</div>
        <div>
          <h3 className="text-blue-300 font-semibold mb-2">Connect Your Bank Account to Earn Money</h3>
          <p className="text-gray-300 text-sm mb-4">
            <strong>Simple setup:</strong> Connect your bank account to automatically receive 50% of your marketplace game sales. 
            Stripe handles all the payments securely - you just get paid automatically!
          </p>
          
          <div className="bg-blue-800/30 rounded-lg p-4 mb-4">
            <h4 className="text-blue-200 font-medium mb-2">What You'll See:</h4>
            <div className="space-y-1 text-sm text-gray-300">
              <div>• A secure Stripe page (this is normal and safe)</div>
              <div>• Simple form asking for your bank details</div>
              <div>• No business registration - just your personal info</div>
            </div>
          </div>
          
          <div className="space-y-2 mb-4">
            <div className="flex items-center text-sm text-gray-400">
              <span className="text-green-400 mr-2">✓</span>
              Money goes straight to your bank automatically
            </div>
            <div className="flex items-center text-sm text-gray-400">
              <span className="text-green-400 mr-2">✓</span>
              No manual payments or waiting
            </div>
            <div className="flex items-center text-sm text-gray-400">
              <span className="text-green-400 mr-2">✓</span>
              Secure Stripe processing (used by millions)
            </div>
          </div>
          
          <button
            onClick={setupConnectAccount}
            disabled={settingUp}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-6 py-3 rounded font-medium"
          >
            {settingUp ? 'Connecting...' : 'Connect My Bank Account'}
          </button>
          
          <p className="text-xs text-gray-500 mt-2">
            Takes 2-3 minutes • Secure Stripe processing • Automatic payments
          </p>
          
          <div className="bg-gray-700/50 rounded-lg p-3 mt-3">
            <p className="text-xs text-gray-400">
              <strong>What You'll See:</strong> You'll go to a secure Stripe page to connect your bank account. 
              This is just like connecting your bank to Venmo or PayPal - you're not starting a business, 
              just setting up automatic payments when people buy your games!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StripeConnectSetup;
